import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DeskNotification } from '@bbbk/schema';
import { api } from '../../lib/api';
import { useMutationTick } from '../../lib/mutationBus';
import { useRecordModal, type RecordFocus } from '../search/RecordModalProvider';

// notifications-daily-stack — ONE READ FOR THE WHOLE SHELL. The stack on the Daily and the bell in
// the top bar both draw from this provider, so the count costs the desk one request, not one per
// surface. It refreshes every ten seconds while the tab is visible, when the tab comes back, and after
// any desk write — which is how a bubble answered in the booking modal lifts away (AC6) and a new
// one lands (AC7) without anyone reloading the page.

/**
 * HOW OFTEN THE DESK LOOKS (Freddi, 2026-10-14: "make sure the desk is constantly fetching these bubbles
 * so if any arrive while were on the desk they automatically appear"). Ten seconds while the tab is
 * visible — one small read per open desk, the same read the bell uses — and at once whenever the
 * window comes back into focus or the connection returns. A hidden tab does not poll; it catches up
 * the moment it is looked at. Was 30s; a push channel (a Durable Object) is the step after this.
 */
export const POLL_MS = 10_000;

interface DeskNotificationsApi {
  events: DeskNotification[];
  /** The id that arrived since the last read, for the arrival motion; null once it has landed. */
  landingId: string | null;
  /** Read for the desk, then open its booking — or, for a card with no stay, the household (AC5). */
  open: (e: DeskNotification) => void;
}

/**
 * THE CARD A BUBBLE OPENS (Freddi, 2026-10-14: "clicking the bubble to open should open the modal and
 * expand the card in question like moneys or vaccinations"). A screenshot is the Money card's; a card
 * is the Vaccinations card's, on its dog's row. A request opens on the Request card, which has no fold,
 * and a registration on the household — nothing to unfold for either.
 */
export function focusFor(e: DeskNotification): RecordFocus | null {
  if (e.kind === 'screenshot') return { card: 'money', dogId: null };
  if (e.kind === 'card') return { card: 'vax', dogId: e.dogId };
  return null;
}

/**
 * A BUBBLE JUST OPENED STAYS READ (PR #271 review, P3). The read-mark is optimistic; a refresh that left
 * before it landed comes back with readAt null and would put the bubble back to unread for a poll. The
 * desk keeps its own reads until the server returns them read itself — or stops returning the event.
 */
export function keepLocalReads(next: DeskNotification[], local: ReadonlyMap<string, string>): { events: DeskNotification[]; local: Map<string, string> } {
  const left = new Map<string, string>();
  const events = next.map((e) => {
    const mine = local.get(e.id);
    if (!mine) return e;
    if (e.readAt) return e;
    left.set(e.id, mine);
    return { ...e, readAt: mine };
  });
  return { events, local: left };
}

const Ctx = createContext<DeskNotificationsApi | null>(null);

/** null outside the provider — the staff-only shell has no notifications. */
export function useDeskNotifications(): DeskNotificationsApi | null {
  return useContext(Ctx);
}

export function DeskNotificationsProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<DeskNotification[]>([]);
  const [landingId, setLandingId] = useState<string | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const localReads = useRef<Map<string, string>>(new Map());
  const record = useRecordModal();
  const tick = useMutationTick();

  const load = useCallback(async () => {
    try {
      const res = await api<{ events: DeskNotification[] }>('/notifications', { method: 'GET', cache: 'reload' });
      const next = res.data?.events ?? [];
      // The first read is what was already there; only what arrives after it lands.
      if (seen.current) {
        const fresh = next.find((e) => !seen.current!.has(e.id));
        if (fresh) setLandingId(fresh.id);
      }
      seen.current = new Set(next.map((e) => e.id));
      const merged = keepLocalReads(next, localReads.current);
      localReads.current = merged.local;
      setEvents(merged.events);
    } catch {
      // A failed refresh keeps what the desk already shows; the next tick tries again.
    }
  }, []);

  useEffect(() => { void load(); }, [load, tick]);
  useEffect(() => {
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') void load(); }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [load]);
  useEffect(() => {
    if (!landingId) return;
    const t = window.setTimeout(() => setLandingId(null), 600);
    return () => window.clearTimeout(t);
  }, [landingId]);

  const open = useCallback((e: DeskNotification) => {
    if (!e.readAt) {
      const at = new Date().toISOString();
      localReads.current.set(e.id, at);
      setEvents((all) => all.map((x) => (x.id === e.id ? { ...x, readAt: at } : x)));
      void api(`/notifications/${e.id}/read`, { method: 'POST', quiet: true }).catch(() => undefined);
    }
    if (e.bookingId) record?.openBooking(e.bookingId, undefined, focusFor(e));
    else if (e.ownerId) record?.openOwner(e.ownerId, undefined, focusFor(e));
  }, [record]);

  const value = useMemo(() => ({ events, landingId, open }), [events, landingId, open]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
